/** Only a finalized, indexed creator-token buy is eligible for Discover promotion. */
export type CreatorBuy = {
  identity: string;
  tokenId: string;
  chain: string;
  block: bigint;
  transaction: number;
  event: number;
  kind: 'creator_buy' | 'creator_sell' | 'receipt_acquisition';
  finalized: boolean;
  successful: boolean;
  verified: boolean;
};
export type BuyFeedState = {
  seen: ReadonlySet<string>;
  history: readonly CreatorBuy[];
  latest: ReadonlyMap<string, CreatorBuy>;
  watermark: ReadonlyMap<string, CreatorBuy>;
};
export const emptyBuyFeed = (): BuyFeedState => ({ seen: new Set(), history: [], latest: new Map(), watermark: new Map() });

export function compareCreatorBuys(a: CreatorBuy, b: CreatorBuy): number {
  return a.block > b.block ? 1 : a.block < b.block ? -1 : 0;
}

export function acceptCreatorBuy(state: BuyFeedState, buy: CreatorBuy): BuyFeedState {
  if (typeof buy.identity !== 'string' || typeof buy.tokenId !== 'string' || typeof buy.chain !== 'string' ||
    !buy.identity.trim() || !buy.tokenId.trim() || !buy.chain.trim() ||
    !buy.identity.startsWith(`${buy.chain}:`) || typeof buy.block !== 'bigint' || buy.block < 0n ||
    !Number.isSafeInteger(buy.transaction) || !Number.isSafeInteger(buy.event) ||
    buy.transaction < 0 || buy.event < 0 ||
    buy.kind !== 'creator_buy' || !buy.finalized || !buy.successful || !buy.verified ||
    state.seen.has(buy.identity)) return state;
  const previous = state.latest.get(buy.tokenId);
  const watermark = state.watermark.get(buy.chain);
  const seen = new Set(state.seen);
  seen.add(buy.identity);
  const history = [...state.history, buy];
  // Retain backfill as history, but only a new block may outrank observed activity.
  if (watermark && buy.block < watermark.block) return { ...state, seen, history };
  if (previous && (buy.chain !== previous.chain || buy.block <= previous.block)) return { ...state, seen, history };
  const latest = new Map(state.latest);
  latest.set(buy.tokenId, buy);
  const nextWatermark = new Map(state.watermark);
  if (!watermark || buy.block > watermark.block) nextWatermark.set(buy.chain, buy);
  return { seen, history, latest, watermark: nextWatermark };
}

/** Establish the observed history without presenting it as a new live update. */
export const seedCreatorBuys = (state: BuyFeedState, buys: CreatorBuy[]): BuyFeedState =>
  buys.reduce(acceptCreatorBuy, state);

export function sortByLatestBuy<T extends { id: string }>(rows: T[], state: BuyFeedState): T[] {
  return [...rows].sort((a, b) => {
    const left = state.latest.get(a.id), right = state.latest.get(b.id);
    if (left && right) return compareCreatorBuys(right, left);
    return left ? -1 : right ? 1 : 0;
  });
}
